import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Card from "../../components/shared/Card/Card";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import SelectInput from "../../components/shared/Inputs/SelectInput";
import useNotification from "../../components/notifications/useNotifications";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useAuth } from "../../contexts/AuthContext";
import useTickets from "../../data/useTickets";
import { ICreateTicketRequest, TicketPriority, TicketType } from "../../models/Ticket";
import { formatTicketDescription, formatTicketSubject } from "../../utils/fixShoutingCase";

export default function CreateTicket() {
  const { t } = useTranslation("tickets");
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { userId, customerId } = useAuth();
  const { createTicket, uploadAttachment } = useTickets();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const maxAttachmentSizeBytes = 100 * 1024 * 1024;

  const priorityOptions = [
    { id: TicketPriority.Low, name: t("Low") },
    { id: TicketPriority.Medium, name: t("Medium") },
    { id: TicketPriority.High, name: t("High") },
    { id: TicketPriority.Urgent, name: t("Urgent") },
  ];

  const typeOptions = [
    { id: TicketType.Bug, name: t("Bug") },
    { id: TicketType.Feature, name: t("Feature") },
  ];

  const { register, handleSubmit, setValue, watch } = useForm<ICreateTicketRequest>({
    defaultValues: {
      subject: "",
      description: "",
      priority: TicketPriority.Medium,
      type: TicketType.Bug,
    },
  });

  // Stop customers raising tickets in full caps to jump the queue
  const fixCasing = (
    field: "subject" | "description",
    format: (text: string) => string
  ) => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const current = e.target.value ?? "";
    const fixed = format(current);
    if (fixed === current) return;

    e.target.value = fixed;
    setValue(field, fixed, { shouldDirty: true });
  };

  useEffect(() => {
    setInfo({
      name: t("Create Support Ticket"),
      desc: t("Raise a new support request"),
    });
  }, [setInfo, t]);

  const onSubmit = async (data: ICreateTicketRequest) => {
    if (!data.subject?.trim() || !data.description?.trim()) {
      addNotification({
        variant: "warn",
        primaryText: t("Please enter a subject and description"),
      });
      return;
    }

    const oversizedFile = selectedFiles.find((file) => file.size > maxAttachmentSizeBytes);
    if (oversizedFile) {
      addNotification({
        variant: "warn",
        primaryText: t("{{name}} exceeds the 100MB limit", { name: oversizedFile.name }),
      });
      return;
    }

    const payload: ICreateTicketRequest = {
      ...data,
      customerId: customerId ?? undefined,
      createdByUserId: userId ?? undefined,
    };

    try {
      const created = await createTicket.mutateAsync(payload);
      const createdTicketId = created?.data?.id;

      if (createdTicketId && selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const fileData = new FormData();
          fileData.append("file", file);
          await uploadAttachment.mutateAsync({ ticketId: createdTicketId, data: fileData });
        }
      }

      addNotification({
        variant: "success",
        primaryText: t("Ticket created successfully"),
      });
      navigate("/tickets");
    } catch (error: any) {
      addNotification({
        variant: "error",
        primaryText: error?.message || t("Failed to create ticket"),
      });
    }
  };

  return (
    <Card containerClassName="mt-4">
      <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
        <TextInput
          label={t("Subject")}
          register={register}
          registerName="subject"
          maxLength={200}
          required
          onBlur={fixCasing("subject", formatTicketSubject)}
        />

        <SelectInput
          label={t("Type")}
          register={register}
          registerName="type"
          options={typeOptions}
          setValue={setValue}
          watch={watch}
        />

        <SelectInput
          label={t("Priority")}
          register={register}
          registerName="priority"
          options={priorityOptions}
          setValue={setValue}
          watch={watch}
        />

        <TextInput
          label={t("Description")}
          register={register}
          registerName="description"
          richText
          rows={6}
          required
          onBlur={fixCasing("description", formatTicketDescription)}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700">{t("Attachments")}</label>
          <input
            type="file"
            multiple
            className="mt-1 block w-full text-sm text-gray-700"
            onChange={(e) => setSelectedFiles(Array.from(e.target.files ?? []))}
          />
          <p className="mt-1 text-xs text-gray-500">{t("Add files to include with this ticket (optional).")}</p>
          <p className="text-xs text-gray-500">{t("Maximum file size: 100MB per attachment.")}</p>
          {selectedFiles.length > 0 ? (
            <ul className="mt-2 space-y-1 text-xs text-gray-600">
              {selectedFiles.map((file) => (
                <li key={`${file.name}-${file.lastModified}`}>- {file.name}</li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="flex justify-end gap-2">
          <ButtonInput
            label={t("Cancel")}
            isSubmit={false}
            classes="!bg-gray-200 !text-gray-700 hover:!bg-gray-300"
            onClick={() => navigate("/tickets")}
          />
          <ButtonInput
            label={t("Create Ticket")}
            isSubmit
            classes=""
            onClick={undefined}
            isLoading={createTicket.isLoading || uploadAttachment.isLoading}
          />
        </div>
      </form>
    </Card>
  );
}
